import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Crown, Headphones, Sparkles, Clock, Shield, Zap } from 'lucide-react'
import SEO from '../../components/SEO'

const TechnologyConcierge = () => {
  const features = [
    {
      icon: Headphones,
      title: 'Personal Technology Advisor',
      description: 'Your dedicated technology advisor available 24/7 for any question, issue, or need.'
    },
    {
      icon: Zap,
      title: 'Rapid Response',
      description: 'Immediate support when you need it most. No waiting, no tickets, just solutions.'
    },
    {
      icon: Sparkles,
      title: 'Proactive Assistance',
      description: 'We anticipate your needs before you ask, keeping you ahead of technology trends.'
    },
    {
      icon: Shield,
      title: 'Complete Discretion',
      description: 'Your privacy and security are paramount. All interactions are completely confidential.'
    },
    {
      icon: Clock,
      title: 'Always Available',
      description: 'Round-the-clock access to expert support across all time zones and locations.'
    },
    {
      icon: Crown,
      title: 'VIP Treatment',
      description: 'Experience service worthy of your status with personalized, white-glove attention.'
    }
  ]

  const services = [
    'Personal technology consultation and planning',
    'Device setup and configuration',
    'Software recommendations and installations',
    'Troubleshooting and technical support',
    'Technology training and education',
    'Vendor liaison and coordination',
    'Technology procurement guidance',
    'Digital lifestyle management'
  ]

  return (
    <>
      <SEO 
        title="Technology Concierge - Praetorian Works"
        description="Personal technology concierge services for HNW and UHNW clients. 24/7 dedicated support, expert guidance, and white-glove service for all your technology needs."
        keywords="technology concierge, personal tech support, VIP IT services, luxury technology assistance, private technology advisor"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Crown className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Technology
                <span className="text-primary-500 ml-4">Concierge</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Your personal technology advisor, available 24/7. Experience unparalleled support 
                with dedicated experts who understand your unique needs and lifestyle.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-secondary-800 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services List */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
                Comprehensive <span className="text-primary-500">Support</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {services.map((service, index) => (
                  <motion.div
                    key={service}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.05 * index }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-neutral-300 text-lg">{service}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Experience Exceptional Service
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Discover how our technology concierge service can simplify your digital life 
                and provide peace of mind.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Schedule a Consultation
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default TechnologyConcierge
